import React, { useEffect, useState } from "react";
import AlterationForm from "../../model/AlterationForm";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useNavigate } from "react-router-dom";
import { useAlerts } from "../../contexts/AlertsContext";
import Material from "../../model/Material";
import APIEndPoints from "../../utils/APIEndPoints";
import SearchFilterCheckboxNumber from "./SearchFilterCheckboxNumber";
import SearchResult from "../../model/SearchResult";
import SearchCriteria from "../../model/SearchCriteria";
import PagedData from "../../model/PagedData";
import SearchFilterCheckboxNumberGroup from "./SearchFilterCheckboxNumberGroup";
import SearchResultsItems from "./SearchResultsItems";
import SearchFilterCard from "./SearchFilterCard";
import loadSearchCriteria from "../../utils/LoadSearchCriteria";
import ChemiNovaEntityType from "../../model/enums/ChemiNovaEntityType";
import SearchFilterCheckboxString from "./SearchFilterCheckboxString";
import chemiNovaEntityTypeDisplay from "../../utils/ChemiNovaEntityTypeDisplay";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import SearchFilterString from "../../model/SearchFilterString";

function Search() {
  // set the data
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>(
    SearchCriteria.EmptyInstance()
  );
  const [searchResult, setSearchResult] = useState<SearchResult>(
    SearchResult.EmptyInstance()
  );
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // manage loading animation
  const [loading, setLoading] = useState(true);
  // information about alteration forms
  const [alterationForms, setAlterationForms] = useState<AlterationForm[]>([]);
  // information about materials
  const [materials, setMaterials] = useState<Material[]>([]);
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        const alter = await apiClient?.get<PagedData<AlterationForm[]>>({
          url: APIEndPoints.ALTERATION_FORMS.ALL,
        });
        setAlterationForms(alter.items);

        const mat = await apiClient?.get<PagedData<Material[]>>({
          url: APIEndPoints.MATERIALS.ALL,
        });
        setMaterials(mat.items);

        setSearchCriteria(loadSearchCriteria());

        const results = await apiClient?.post<SearchResult>({
          url: APIEndPoints.SEARCH.PAGED(searchCriteria),
          body: searchCriteria,
        });
        setSearchResult(results);
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient]);

  const formInputChange = async (
    inputID: string,
    newValue: string | boolean | Set<number> | Set<string>
  ) => {
    const searchCriteriaCopy = JSON.parse(JSON.stringify(searchCriteria));
    if (
      inputID == "alteration_id" ||
      inputID == "material_id" ||
      inputID == "types"
    ) {
      searchCriteriaCopy[inputID] = Array.from(newValue as Set<number>);
    } else {
      searchCriteriaCopy[inputID] = newValue;
    }
    setSearchCriteria(searchCriteriaCopy as SearchCriteria);
    // save the search criteria on local storage
    localStorage.setItem("search_criteria", JSON.stringify(searchCriteriaCopy));
  };

  const checkForEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      performSearch();
    }
  };

  const performSearch = async () => {
    const results = await apiClient?.get<SearchResult>({
      url: APIEndPoints.SEARCH.PAGED(searchCriteria),
      body: searchCriteria,
    });
    setSearchResult(results);
  };

  const entityTypesForFiltering = [
    {
      entity_type: ChemiNovaEntityType.CHELEMENT,
      results: searchResult.chelements_no,
    },
    {
      entity_type: ChemiNovaEntityType.E3DMODEL,
      results: searchResult.e3dmodels_no,
    },
    {
      entity_type: ChemiNovaEntityType._3DINSTANCE,
      results: searchResult.e3dinstances_no,
    },
    {
      entity_type: ChemiNovaEntityType.ANNOTATION,
      results: searchResult.annotations_no,
    },
  ];

  const types_list = entityTypesForFiltering.map((item) => {
    return new SearchFilterString(
      item.entity_type,
      chemiNovaEntityTypeDisplay(item.entity_type, false),
      item.results
    );
  });

  return (
    <div className="search-page">
      <div className="search-left">
        <h2>Filter results by:</h2>
        <SearchFilterCard title="Entity type">
          <SearchFilterCheckboxString
            name="types"
            onChange={formInputChange}
            data={types_list}
            selected={new Set(searchCriteria.types)}
          />
        </SearchFilterCard>

        <SearchFilterCard title="Material">
          <SearchFilterCheckboxNumber
            name="material_id"
            onChange={formInputChange}
            data={materials}
            selected={new Set(searchCriteria.material_id)}
          />
        </SearchFilterCard>
        <SearchFilterCard title="Alteration form">
          {alterationForms.map((item) => {
            return (
              <SearchFilterCheckboxNumberGroup
                name="alteration_id"
                onChange={formInputChange}
                data={item}
                selected={new Set(searchCriteria.alteration_id)}
              />
            );
          })}
        </SearchFilterCard>
      </div>
      <div className="search-right">
        <div className="search-bar">
          <input
            type="text"
            name="search"
            id="search"
            value={searchCriteria.search}
            className="search-keyword"
            placeholder="Search ChemiNova repository"
            onChange={(e) => formInputChange("search", e.target.value)}
            onKeyDown={checkForEnter}
            required
          />
          <Button
            label="Search"
            purpose={ButtonAction.SEARCH}
            onClick={performSearch}
          />
        </div>
        <div className="search-sorting"></div>
        <div className="search-results">
          <SearchResultsItems data={searchResult} />
        </div>
        <div className="search-pages"></div>
      </div>
    </div>
  );
}

export default Search;
